var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { AwsUi } from '../../../core/runtime/src';
import InputController from './controllers/input-controller';
import BaseInput from './base-input';
export var Input = AwsUi.__addComponent('awsui-input', {
    releaseStatus: 'stable',
    callbacks: {
        initialized: function () {
            this.__initializeController();
            if (!this.controlId) {
                this.controlId = this.__controller.generatedId;
            }
        },
        attached: function () {
            this.__controller.addListeners();
        },
        detached: function () {
            this.__controller.removeListeners();
        }
    },
    events: {
        // Fired when input focus is removed from the UI control.
        blur: {
            bubbles: true,
            cancelable: false
        },
        // Mimics the native `change` event on inputs.
        // The event `detail` contains the current value of the field.
        change: {
            bubbles: true,
            cancelable: false,
            detailType: 'Input.ChangeDetail'
        },
        // Fired when input focus is set onto the UI control.
        focus: {
            bubbles: true,
            cancelable: false
        },
        // Mimics the native `input` event on inputs.
        // The event `detail` contains the current value of the field.
        input: {
            bubbles: true,
            cancelable: false,
            detailType: 'Input.ChangeDetail'
        },
        // Fired when the underlying native input fires `keydown` event.
        // The event `detail` contains the `keyCode` and information about modifiers (alt, ctrl, ...).
        keydown: {
            bubbles: true,
            cancelable: true,
            detailType: 'Input.KeyDetail'
        },
        // Fired when the underlying native input fires `keyup` event.
        // The event `detail` contains the `keyCode` and information about modifiers (alt, ctrl, ...).
        keyup: {
            bubbles: true,
            cancelable: true,
            detailType: 'Input.KeyDetail'
        },
        // Fired when the underlying native input fires `keypress` event.
        // The event `detail` contains the `key` property and information about modifiers (alt, ctrl, ...).
        keypress: {
            bubbles: true,
            cancelable: true,
            detailType: 'Input.KeyDetail'
        }
    },
    functions: {
        // Sets input focus onto the UI control.
        focus: function () {
            this.node.querySelector('input').focus();
        },
        __initializeController: function () {
            var _this = this;
            this.__controller = new InputController({
                getValue: function () { return _this.value; },
                notifyInput: function (value) {
                    _this.value = value;
                    _this.__fireEvent('input', { value: value });
                },
                notifyChange: function (value) { return _this.__fireEvent('change', { value: value }); },
                focus: function () { return _this.focus(); },
                notifyFocus: function () { return _this.__fireEvent('focus'); },
                notifyBlur: function () { return _this.__fireEvent('blur'); },
                notifyKeyEvent: function (type, detail, extra) { return _this.__fireEvent(type, detail, extra); }
            }, this.node);
        }
    },
    properties: {
        // Text entered into the form element.
        value: {
            type: 'string',
            defaultValue: '',
            onChange: function () {
                this.__controller.valueAttributeChangeHandler(this.value);
            }
        },
        // Type of control to render.
        // Input with `number` type uses the native element behavior, which might be slightly different across browsers.
        type: {
            type: 'string',
            defaultValue: 'text',
            valid: ['text', 'password', 'search', 'number', 'email']
        },
        // The name of the control used in HTML forms.
        name: {
            type: 'string'
        },
        // Placeholder text rendered when the value is an empty string.
        placeholder: {
            type: 'string'
        },
        // Specifies that the input should be disabled, preventing the user from
        // modifying the value and preventing the value from being included in a
        // form submission. A disabled input cannot receive focus.
        //
        // **AngularJS:** Use `data-disabled` instead.
        disabled: {
            type: 'boolean'
        },
        // Specifies that the input should be readonly, preventing the user from
        // modifying the value but including it in a form submission. A readonly input can receive focus.
        //
        // Do not use readonly inputs outside of a form.
        readonly: {
            type: 'boolean'
        },
        // Overrides invalidation state. Usually invalid state comes from wrapping `awsui-form-field` component, however
        // sometimes you need to override its state when you have more than one input within single form-field.
        invalid: {
            type: 'boolean',
            nullable: true
        },
        // Specifies whether browsers should enable autocomplete for this input.
        // In some cases it may be appropriate to disable autocomplete, for example for
        // security-sensitive fields.
        autocomplete: {
            type: 'boolean',
            defaultValue: true
        },
        // Setting this to `true` will disable any native browser capabilities to
        // automatically correct user input, such as `autocorrect` and `autocapitalize`.
        // If unset, behavior will be the default behavior of the respective browser.
        disableBrowserAutocorrect: {
            type: 'boolean',
            defaultValue: false
        },
        // Adds a hint to the browser about the type of data a user may enter into this field.
        // Some devices may render a different virtual keyboard depending on this value.
        // This value may not be supported by all browsers or devices.
        inputmode: {
            type: 'string',
            valid: ['none', 'text', 'decimal', 'numeric', 'tel', 'search', 'email', 'url']
        },
        // Allows you to indicate that the control is to be focused as
        // soon as the `load` event triggers, allowing the user to just start
        // typing without having to manually focus the input. Don't use this option
        // in pages that allow for the field to be scrolled out of the viewport.
        autofocus: {
            type: 'boolean'
        },
        // Id of the internal input. Useful for relating a label element's
        // "for" attribute to this control. It defaults to an automatically generated id.
        controlId: {
            type: 'string',
            announceValue: true,
            onChange: function () {
                if (!this.controlId) {
                    this.controlId = this.__controller.generatedId;
                }
            }
        },
        // Adds an aria label on the native input.
        label: {
            type: 'string'
        },
        // Adds aria-labelledby on the native input. Use this only with form fields that
        // contain multiple controls under the same label.
        // 1. Define a custom id inside the label
        // 2. Refer to that label from every single control under that label using this property
        ariaLabelledby: {
            type: 'string'
        },
        // Adds aria-describedby on the native input. Use this only with form fields that
        // contain multiple controls under the same label.
        // 1. Define custom ids inside the description, hint and error text
        // 2. Refer to these from every single control under that label using this property
        // 3. Refer to any other hint/description text that you provide
        ariaDescribedby: {
            type: 'string',
            nullable: true
        },
        // Adds aria-required on the native input
        ariaRequired: {
            type: 'boolean'
        },
        // A default suggested aria-describedby value that can be pushed down from a FormField
        __suggestedAriaDescribedby: {
            type: 'string'
        },
        __ariaActivedescendant: {
            type: 'string'
        },
        __role: {
            type: 'string'
        },
        __ariaControls: {
            type: 'string'
        },
        __ariaExpanded: {
            type: 'string'
        },
        __ariaHaspopup: {
            type: 'string'
        },
        // Override the `type` attribute on the native input tag, regardless of what type
        // was set on the Input component. This is necessary for implementing combobox patterns
        // where we want the visuals of a search-type Input component, but the underlying
        // native input cannot be of type "search".
        __nativeInputType: {
            type: 'string',
            valid: ['search', 'text']
        }
    },
    wrapperSupport: {
        ngModel: {
            value: 'value',
            event: 'input'
        }
    },
    render: function (m, h) {
        var searchAttributes = this.type === 'search'
            ? this.__controller.getSearchAttributesForBaseInput(!this.disabled && !this.readonly && this.value)
            : null;
        var a11yAttributes = h.copyDefined({
            'aria-describedby': this.ariaDescribedby || this.__suggestedAriaDescribedby || undefined,
            'aria-label': this.label,
            'aria-labelledby': this.ariaLabelledby,
            'aria-expanded': this.__ariaExpanded,
            'aria-haspopup': this.__ariaHaspopup,
            'aria-controls': this.__ariaControls,
            'aria-activedescendant': this.__ariaActivedescendant
        });
        return (AwsUi.__createElement(BaseInput, __assign({}, searchAttributes, { value: this.value, type: this.__nativeInputType || this.type, autocomplete: this.autocomplete, disabled: this.disabled, readonly: this.readonly, disableBrowserAutocorrect: this.disableBrowserAutocorrect, inputmode: this.inputmode, "aria-invalid": this.invalid, "aria-required": this.ariaRequired, additionalAttrs: __assign(__assign({}, a11yAttributes), { role: this.__role, name: this.name, id: this.controlId, placeholder: this.placeholder, autofocus: this.autofocus }) })));
    }
});
//# sourceMappingURL=input.js.map